import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Badge,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  FileDownload as DownloadIcon,
  Edit as EditIcon,
  NotificationsActive as AlarmIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Draw as DrawIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { documentService } from '../services/documentService';
import { useAuthStore } from '../stores/authStore';
import { IQuestionnaireExpiredAssignment } from '../types/questionnaire';

interface AssignedDocumentsProps {
  /** API path to fetch assigned wills from. Defaults to the general assigned-versions endpoint. */
  endpoint?: string;
  /** Page heading. */
  title?: string;
  /** Page sub-heading. */
  subtitle?: string;
  /** Column header for the email/person column. Defaults to role-based ('Owner' for EL, 'Assigned To' otherwise). */
  emailColumnLabel?: string;
  /** When true the email column shows ownerEmail; when false it shows assigneeEmail. Defaults to isEL check. */
  showOwnerEmail?: boolean;
}

type AssignedWillRow = {
  id: string;
  submissionId: string;
  templateTitle: string;
  assigneeEmail?: string;
  ownerEmail?: string;
  ownerName?: string;
  status: 'completed' | 'expired' | 'pending' | 'in-progress';
  version?: number;
  completedAt?: string;
  expiresAt?: string;
  assignedAt?: string;
  canEdit?: boolean;
  documentTemplateId?: string;
  formTemplateId?: string;
  canEditTemplate?: boolean;
  canAssignExecutor?: boolean;
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const AssignedDocuments: React.FC<AssignedDocumentsProps> = ({
  endpoint = '/documents/assigned-versions',
  title = 'Assigned Documents',
  subtitle = 'Documents completed or expired via email assignments.',
  emailColumnLabel,
  showOwnerEmail,
}) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isEL = user?.userType === 'EL';

  // Resolve display config — props override the role-based defaults
  const resolvedEmailColumnLabel = emailColumnLabel ?? (isEL ? 'Owner' : 'Assigned To');
  const resolvedShowOwnerEmail = showOwnerEmail ?? isEL;

  const [rawData, setRawData] = useState<any[]>([]);
  const [expiredAssignments, setExpiredAssignments] = useState<IQuestionnaireExpiredAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [launchingSubmissionId, setLaunchingSubmissionId] = useState<string | null>(null);

  // Share with Estate Executor dialog state
  const [shareExecutorTargetRow, setShareExecutorTargetRow] = useState<AssignedWillRow | null>(null);
  const [executors, setExecutors] = useState<Array<{ _id: string; name: string; email: string }>>([]);
  const [loadingExecutors, setLoadingExecutors] = useState(false);
  const [selectedExecutorEmail, setSelectedExecutorEmail] = useState('');
  const [sharingExecutor, setSharingExecutor] = useState(false);
  const [shareExecutorError, setShareExecutorError] = useState('');
  const [shareExecutorSuccess, setShareExecutorSuccess] = useState('');

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      // Only fetch expired assignments on the default general-purpose endpoint
      const fetchExpired = endpoint === '/documents/assigned-versions' && user?.role === 'PLUS';
      // Start all requests together; results are applied in the same order and with the
      // same failure handling as when they ran one after another.
      const [response, expiredResponse] = await Promise.allSettled([
        api.get<any[]>(endpoint),
        fetchExpired ? api.get<IQuestionnaireExpiredAssignment[]>('/documents/expired-assignments') : Promise.resolve(null),
      ]);
      if (response.status === 'rejected') throw response.reason;
      setRawData(response.value.data);
      setError('');
      if (fetchExpired && expiredResponse.status === 'fulfilled' && expiredResponse.value) {
        setExpiredAssignments(expiredResponse.value.data);
      } else {
        setExpiredAssignments([]);
      }
    } catch (err) {
      setError('Failed to load assigned documents.');
    } finally {
      setLoading(false);
    }
  }, [endpoint, user?.role]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const rows = useMemo<AssignedWillRow[]>(() => {
    const mainRows: AssignedWillRow[] = rawData.map((item: any) => {
      // Backend now returns canonical documentId/documentTemplateId; fall back to the
      // legacy submissionId/formTemplateId while both are present mid-rollout.
      const submissionId = item.documentId ?? item.submissionId;
      return {
        // Support both IQuestionnaireAssignedVersion (version field) and
        // IEstateLawyerAssignedWill (version field — same name after API normalisation)
        id: `${submissionId}-${item.version || 'curr'}`,
        submissionId,
        templateTitle: item.templateTitle,
        assigneeEmail: item.assigneeEmail,
        ownerEmail: item.ownerEmail,
        ownerName: item.ownerName,
        status: item.status,
        version: item.version,
        completedAt: item.completedAt,
        expiresAt: item.expiresAt,
        assignedAt: item.assignedAt,
        canEdit: item.canEdit,
        documentTemplateId: item.documentTemplateId,
        formTemplateId: item.documentTemplateId ?? item.formTemplateId,
        canEditTemplate: item.canEditTemplate,
        canAssignExecutor: item.canAssignExecutor,
      };
    });

    const filteredRows = mainRows.filter((row) => {
      const term = searchTerm.toLowerCase();
      return (
        row.templateTitle.toLowerCase().includes(term) ||
        (row.assigneeEmail || '').toLowerCase().includes(term) ||
        (row.ownerEmail || '').toLowerCase().includes(term) ||
        row.status.toLowerCase().includes(term)
      );
    });

    return filteredRows.sort((a, b) => {
      const timeA = new Date(a.completedAt || a.expiresAt || a.assignedAt || 0).getTime();
      const timeB = new Date(b.completedAt || b.expiresAt || b.assignedAt || 0).getTime();
      return timeB - timeA;
    });
  }, [rawData, searchTerm]);

  const expiredCount = user?.role === 'PLUS' ? expiredAssignments.length : 0;

  const handleOpenEdit = async (submissionId: string) => {
    try {
      setLaunchingSubmissionId(submissionId);
      const response = await api.post<{ token: string }>(`/documents/${submissionId}/self-access-link`);
      navigate(`/assigned-documents/open?token=${response.data.token}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to open edit link.');
    } finally {
      setLaunchingSubmissionId(null);
    }
  };

  const handleOpenExecutorSign = (submissionId: string) => {
    navigate(`/assigned-documents/${submissionId}`);
  };

  const handleOpenShareExecutorDialog = async (row: AssignedWillRow) => {
    setShareExecutorTargetRow(row);
    setSelectedExecutorEmail('');
    setShareExecutorError('');
    setShareExecutorSuccess('');
    setLoadingExecutors(true);
    try {
      setExecutors(await documentService.getExecutors());
    } catch {
      setShareExecutorError('Failed to load Estate Executors.');
    } finally {
      setLoadingExecutors(false);
    }
  };

  const handleShareExecutorSubmit = async () => {
    if (!shareExecutorTargetRow || !selectedExecutorEmail) return;

    setSharingExecutor(true);
    setShareExecutorError('');
    setShareExecutorSuccess('');

    try {
      await documentService.assignExecutor(shareExecutorTargetRow.submissionId, {
        executorEmail: selectedExecutorEmail,
        expiresInDays: 7,
      });
      setShareExecutorSuccess('Document successfully shared with executor.');
      fetchSubmissions();
      setTimeout(() => {
        setShareExecutorTargetRow(null);
      }, 1200);
    } catch (err: any) {
      setShareExecutorError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to share document with executor.'
      );
    } finally {
      setSharingExecutor(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {user?.role === 'PLUS' && (
            <Tooltip title="Expired assignments">
              <IconButton
                onClick={() => navigate('/assigned-documents/alarms')}
                color={expiredCount > 0 ? 'error' : 'default'}
              >
                <Badge badgeContent={expiredCount} color="error">
                  <AlarmIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {user?.role === 'PLUS' && expiredCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {expiredCount} assigned document{expiredCount === 1 ? '' : 's'} expired without completion. Review them in Alarm.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }} elevation={1}>
        <TextField
          size="small"
          placeholder="Search by title, assignee, or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <IconButton onClick={() => fetchSubmissions()} title="Refresh list">
          <RefreshIcon />
        </IconButton>
      </Paper>

      <TableContainer component={Paper} elevation={1}>
        <Table sx={{ minWidth: 750 }}>
          <TableHead sx={{ backgroundColor: 'action.hover' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{resolvedEmailColumnLabel}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Revision</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Completed/Expired</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Loading documents...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography variant="h6" color="text.secondary">
                    No assigned documents yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {row.templateTitle}
                      </Typography>
                      {row.status === 'expired' && <AlarmIcon color="error" fontSize="small" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {resolvedShowOwnerEmail
                      ? (row.ownerName ? `${row.ownerName} (${row.ownerEmail || '-'})` : (row.ownerEmail || '-'))
                      : (row.assigneeEmail || '-')}
                  </TableCell>
                  <TableCell>
                    {row.status === 'completed' ? (
                      <Chip
                        icon={<CompletedIcon />}
                        label="Completed"
                        color="success"
                        size="small"
                      />
                    ) : row.status === 'in-progress' ? (
                      <Chip
                        label="In Progress"
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    ) : row.status === 'pending' ? (
                      <Chip
                        label="Pending"
                        color="warning"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<AlarmIcon />}
                        label="Expired"
                        color="error"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {row.version ? <Chip label={`v${row.version}`} size="small" variant="filled" /> : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(row.completedAt || row.expiresAt || row.assignedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title="View Document">
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="View"
                          onClick={() => {
                            const versionParam = row.version ? `?version=${row.version}` : '';
                            navigate(`/documents/${row.submissionId}${versionParam}`);
                          }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download Document">
                        <IconButton
                          size="small"
                          color="default"
                          aria-label="Download"
                          onClick={() => {
                            const versionParam = row.version ? `?version=${row.version}&download=true` : '?download=true';
                            navigate(`/documents/${row.submissionId}${versionParam}`);
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {row.canEdit && (
                        <Tooltip title="Edit Content">
                          <span>
                            <IconButton
                              size="small"
                              color="secondary"
                              aria-label="Edit"
                              onClick={() => handleOpenEdit(row.submissionId)}
                              disabled={launchingSubmissionId === row.submissionId}
                            >
                              {launchingSubmissionId === row.submissionId ? <CircularProgress size={16} color="inherit" /> : <EditIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {isEL && row.canAssignExecutor && (
                        <Tooltip title="Share with Estate Executor">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Share with Estate Executor"
                            onClick={() => handleOpenShareExecutorDialog(row)}
                          >
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {row.status !== 'expired' && (
                        <Tooltip title="Open Assigned Document & Sign">
                          <span>
                            <IconButton
                              size="small"
                              color="secondary"
                              aria-label="Sign"
                              onClick={() => handleOpenExecutorSign(row.submissionId)}
                              disabled={launchingSubmissionId === row.submissionId}
                            >
                              <DrawIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Share Document with Estate Executor Dialog */}
      <Dialog
        open={Boolean(shareExecutorTargetRow)}
        onClose={() => !sharingExecutor && setShareExecutorTargetRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Share Document with Estate Executor</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Select an Estate Executor to share <strong>{shareExecutorTargetRow?.templateTitle}</strong> with. They will receive an email invitation to view and sign the document.
          </Typography>

          {shareExecutorError && <Alert severity="error" sx={{ mb: 2 }}>{shareExecutorError}</Alert>}
          {shareExecutorSuccess && <Alert severity="success" sx={{ mb: 2 }}>{shareExecutorSuccess}</Alert>}

          {loadingExecutors ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : executors.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No registered Estate Executors found.
            </Alert>
          ) : (
            <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
              <InputLabel id="select-executor-label">Select Estate Executor</InputLabel>
              <Select
                labelId="select-executor-label"
                value={selectedExecutorEmail}
                label="Select Estate Executor"
                onChange={(e) => setSelectedExecutorEmail(e.target.value)}
                disabled={sharingExecutor}
              >
                {executors.map((executor) => (
                  <MenuItem key={executor._id} value={executor.email}>
                    {executor.name} ({executor.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button disabled={sharingExecutor} onClick={() => setShareExecutorTargetRow(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!selectedExecutorEmail || sharingExecutor}
            onClick={handleShareExecutorSubmit}
          >
            {sharingExecutor ? 'Sharing...' : 'Share Document'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignedDocuments;
